import { Component, OnInit, inject, input, output, signal } from '@angular/core'
import { RouterModule } from '@angular/router'
import {
  LucideGlobe, LucideTrophy, LucideHouse, LucideListVideo, LucideRadio,
  LucideUserRound, LucideCalendarDays, LucideChartColumn, LucideCirclePlay,
  LucideInbox, LucideVideo, LucideShieldCheck, LucideWebhook,
  LucideShirt, LucideUserCog, LucideIdCard, LucideClockAlert, LucideClapperboard, LucideFlag, LucideDownload, LucideUpload,
} from '@lucide/angular'
import { LiveApiService } from '../../../live/live.service'
import { LauncherSection } from './launcher-items'

/**
 * The grid of tiles inside the launcher panel. The navbar owns opening, closing and the
 * phone sheet; this only draws the sections and reports which tile was picked.
 *
 * Tiles are real links with replaceUrl: the open panel holds a history entry (so the phone
 * Back button closes it), and the page a tile opens takes that entry over. `picked` carries
 * whether the tile is the page already open, in which case nothing navigates and the
 * entry has to be stepped back over instead.
 */
@Component({
  selector: 'app-launcher',
  standalone: true,
  imports: [
    RouterModule,
    LucideGlobe, LucideTrophy, LucideHouse, LucideListVideo, LucideRadio,
    LucideUserRound, LucideCalendarDays, LucideChartColumn, LucideCirclePlay,
    LucideInbox, LucideVideo, LucideShieldCheck, LucideWebhook,
    LucideShirt, LucideUserCog, LucideIdCard, LucideClockAlert, LucideClapperboard, LucideFlag, LucideDownload, LucideUpload,
  ],
  template: `
    @for (section of sections(); track section.id) {
      <section class="lsec" [attr.aria-labelledby]="'lsec-' + section.id">
        <h2 class="lsec__title" [id]="'lsec-' + section.id">{{ section.title }}</h2>
        <div class="lgrid">
          @for (tile of section.tiles; track tile.id) {
            <a class="ltile"
               [class]="'tone-' + tile.tone"
               [routerLink]="tile.link"
               [replaceUrl]="true"
               routerLinkActive="ltile--active"
               #rla="routerLinkActive"
               [routerLinkActiveOptions]="{ paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }"
               [attr.aria-label]="tile.id === 'live' && liveNow() ? tile.label + ' (matches on air now)' : null"
               (click)="picked.emit({ navigating: !rla.isActive })">
              <span class="ltile__chip">
                @switch (tile.icon) {
                  @case ('globe') { <svg lucideGlobe [size]="22"></svg> }
                  @case ('trophy') { <svg lucideTrophy [size]="22"></svg> }
                  @case ('home') { <svg lucideHouse [size]="22"></svg> }
                  @case ('teams') { <svg lucideShirt [size]="22"></svg> }
                  @case ('playlists') { <svg lucideListVideo [size]="22"></svg> }
                  @case ('live') { <svg lucideRadio [size]="22"></svg> }
                  @case ('profile') { <svg lucideUserRound [size]="22"></svg> }
                  @case ('matches') { <svg lucideCalendarDays [size]="22"></svg> }
                  @case ('stats') { <svg lucideChartColumn [size]="22"></svg> }
                  @case ('videos') { <svg lucideCirclePlay [size]="22"></svg> }
                  @case ('inbox') { <svg lucideInbox [size]="22"></svg> }
                  @case ('streams') { <svg lucideVideo [size]="22"></svg> }
                  @case ('users') { <svg lucideUserCog [size]="22"></svg> }
                  @case ('players') { <svg lucideIdCard [size]="22"></svg> }
                  @case ('shield') { <svg lucideShieldCheck [size]="22"></svg> }
                  @case ('webhooks') { <svg lucideWebhook [size]="22"></svg> }
                  @case ('clock') { <svg lucideClockAlert [size]="22"></svg> }
                  @case ('library') { <svg lucideClapperboard [size]="22"></svg> }
                  @case ('referees') { <svg lucideFlag [size]="22"></svg> }
                  @case ('downloads') { <svg lucideDownload [size]="22"></svg> }
                  @case ('upload') { <svg lucideUpload [size]="22"></svg> }
                }
                @if (tile.id === 'live' && liveNow()) {
                  <span class="ltile__live" aria-hidden="true"></span>
                }
              </span>
              <span class="ltile__label">{{ tile.label }}</span>
            </a>
          }
        </div>
      </section>
    }
  `,
  styles: [`
    /* Sized against Google's app grid (CC 05/10/2026: it was too big): 84px tiles, 46px chips. */
    :host { display: flex; flex-direction: column; gap: 10px; }

    .lsec {
      border-radius: 22px;
      padding: 12px 8px 8px;
      background: var(--scios-surface-2);
    }
    .lsec__title {
      margin: 0 0 4px;
      padding: 0 10px;
      font-size: 15px;
      font-weight: 600;
      color: var(--scios-text-primary);
    }
    .lgrid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    /* One colour per tile, softened for the chip and ring. Light backgrounds take the
       deeper shade of each so icons keep their contrast. */
    .tone-cyan   { --tone: #06D6F5; }
    .tone-orange { --tone: #F97316; }
    .tone-amber  { --tone: #F59E0B; }
    .tone-green  { --tone: #10B981; }
    .tone-purple { --tone: #A78BFA; }
    .tone-red    { --tone: #EF4444; }
    :host-context([data-theme="light"]) .tone-cyan   { --tone: #0E7490; }  /* teal, not blue, on Light */
    :host-context([data-theme="light"]) .tone-orange { --tone: #C2410C; }
    :host-context([data-theme="light"]) .tone-amber  { --tone: #B45309; }
    :host-context([data-theme="light"]) .tone-green  { --tone: #047857; }
    :host-context([data-theme="light"]) .tone-purple { --tone: #7C3AED; }
    :host-context([data-theme="light"]) .tone-red    { --tone: #DC2626; }

    .ltile {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 7px;
      min-height: 84px;
      padding: 6px 4px;
      border-radius: 16px;
      text-decoration: none;
      color: var(--scios-text-primary);
      -webkit-tap-highlight-color: transparent;
      transition: background-color 150ms ease, transform 150ms ease;
    }
    @media (hover: hover) {
      .ltile:hover { background: color-mix(in srgb, var(--scios-text-primary) 7%, transparent); }
    }
    .ltile:active { transform: scale(0.96); }
    .ltile:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: -2px; }

    .ltile__chip {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 46px;
      height: 46px;
      border-radius: 14px;
      color: var(--tone);
      background: color-mix(in srgb, var(--tone) 15%, transparent);
      box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--tone) 32%, transparent);
    }
    /* The page already open: the chip fills in, like a selected app. */
    .ltile--active .ltile__chip {
      background: color-mix(in srgb, var(--tone) 26%, transparent);
      box-shadow: inset 0 0 0 2px var(--tone);
    }
    .ltile__label {
      max-width: 100%;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.2;
      text-align: center;
    }

    .ltile__live {
      position: absolute;
      top: -3px;
      right: -3px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: var(--live-red);
      border: 2px solid var(--scios-surface-2);
      animation: ltile-pulse calc(1.6s * var(--motion, 1)) infinite;
    }
    @keyframes ltile-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(229, 48, 58, 0.7); }
      70%  { box-shadow: 0 0 0 8px rgba(229, 48, 58, 0); }
      100% { box-shadow: 0 0 0 0 rgba(229, 48, 58, 0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .ltile, .ltile__live { transition: none; animation: none; }
    }
  `],
})
export class AppLauncherComponent implements OnInit {
  private readonly liveApi = inject(LiveApiService)

  readonly sections = input.required<LauncherSection[]>()
  readonly picked = output<{ navigating: boolean }>()

  /** A match is on air: the Live tile pulses, as the LIVE button in the bar does. */
  readonly liveNow = signal(false)

  ngOnInit (): void {
    void this.liveApi.cards()
      .then((cards) => this.liveNow.set(cards.some((c) => c.status === 'live')))
      .catch(() => this.liveNow.set(false))
  }
}
